import React, { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send, Paperclip, Loader2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

export function ChatPanel({ projectId, meRole }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem("siteux_token");

  const uploadMedia = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/projects/${projectId}/messages/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      load();
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/messages`);
      setMessages(data);
    } catch (e) {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/projects/${projectId}/messages`, { text: text.trim() });
      setText("");
      load();
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="chat-panel" className="flex h-[420px] flex-col border border-white/10 bg-black">
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
        {messages.length === 0 && (
          <p className="mt-10 text-center text-sm text-zinc-600">No messages yet. Say hello 👋</p>
        )}
        {messages.map((m) => {
          const mine = m.sender_role === meRole;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] px-3 py-2 text-sm ${mine ? "bg-primary text-white" : "bg-white/5 text-zinc-100"}`}>
                <div className="mb-0.5 font-mono text-[10px] uppercase tracking-wider opacity-60">
                  {m.sender_role === "admin" ? "Sites UX" : m.sender_name}
                </div>
                {m.attachment_path && m.attachment_type === "image" && (
                  <img data-testid="chat-image" src={`${API}/messages/media/${m.attachment_path}?auth=${token}`}
                    alt="attachment" className="mb-1 max-h-56 w-auto rounded border border-white/10" />
                )}
                {m.attachment_path && m.attachment_type === "video" && (
                  <video data-testid="chat-video" src={`${API}/messages/media/${m.attachment_path}?auth=${token}`}
                    controls className="mb-1 max-h-64 w-full rounded border border-white/10" />
                )}
                {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                <div className="mt-1 text-right text-[9px] opacity-50">{format(parseISO(m.created_at), "MMM d, h:mm a")}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2 border-t border-white/10 p-3">
        <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden"
          data-testid="chat-attach-input" onChange={(e) => uploadMedia(e.target.files?.[0])} />
        <Button type="button" data-testid="chat-attach-btn" variant="ghost" onClick={() => fileRef.current?.click()}
          disabled={uploading} className="shrink-0 rounded-sm text-zinc-400 hover:text-white">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
        </Button>
        <Input
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type a message…"
          className="rounded-sm border-white/10 bg-black"
        />
        <Button data-testid="chat-send-btn" onClick={send} disabled={sending || !text.trim()}
          className="rounded-sm bg-primary text-white hover:bg-primary/90">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
